import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import type { CropState } from "../../components/InputPhoto/InputPhotoCropModal"
import type { ImageAsset } from "../../Objects/ImageLibrary/ImageAsset"
import { EImageAssetSourceType } from "../../Enum/EImageAssetSourceType"
import { DeleteRequest, GetBlobRequest, GetRequest, PostFormRequest, PostRequest } from "../../services/ApiClientService"
import { getImageExtensionByContentType } from "../../utils/imageLibraryFile"

interface ImageLibraryResponse {
  images: ImageAsset[]
  maxImagesPerProduct: number
}

interface UseImageLibraryProps {
  enabled?: boolean
  maxSelection?: number
  selectedIds: number[]
  selectedUrls: string[]
  onChange: (ids: number[], urls: string[]) => void | Promise<void>
}

type ImageLibraryCropTarget =
  | {
      mode: "draft"
      assetId: number
      index: number
    }
  | {
      mode: "selected"
      assetId?: number
      index: number
    }

interface ImageLibraryCropState extends CropState {
  target: ImageLibraryCropTarget
}

const getFileExtensionByUrl = (url: string) => {
  const extension = url.split(/[?#]/)[0].match(/\.(png|jpe?g|webp|gif)$/i)?.[0]

  return extension ?? ".jpg"
}

export const useImageLibrary = ({
  enabled = true,
  maxSelection,
  selectedIds,
  selectedUrls,
  onChange,
}: UseImageLibraryProps) => {
  const { t } = useTranslation("genericFoodEntity")

  const { setError } = useError()

  const { enableLoad, disableLoad } = useLoad()

  const [isOpen, setIsOpen] = useState(false)

  const [assets, setAssets] = useState<ImageAsset[]>([])

  const [selectedAssetsMetadataLoaded, setSelectedAssetsMetadataLoaded] = useState(selectedIds.length === 0)

  const [draftIds, setDraftIds] = useState<number[]>([])

  const [maxProductImages, setMaxProductImages] = useState(5)

  const selectionLimit = Math.min(maxProductImages, Math.max(1, maxSelection ?? maxProductImages))

  const [cropState, setCropState] = useState<ImageLibraryCropState | null>(null)

  const selectedAssets = useMemo(
    () => draftIds.map((id) => assets.find((asset) => asset.id === id)).filter((asset): asset is ImageAsset => !!asset),
    [assets, draftIds],
  )

  const selectedImagesSignature = useMemo(
    () => `${selectedIds.join(",")}|${selectedUrls.join(",")}`,
    [selectedIds, selectedUrls],
  )

  const showError = (error: unknown) => {
    const message = error instanceof Error ? error.message : t("general:label_unknown_error_description")
    setError(t("general:label_title_erro"), message)
  }

  const reconcileSelectedIds = (libraryAssets: ImageAsset[]) => {
    return selectedUrls.flatMap((url, index) => {
      const asset = libraryAssets.find((item) => item.url === url)

      const selectedId = selectedIds[index]

      return asset ? [asset.id] : selectedId ? [selectedId] : []
    })
  }

  const uploadImageAsset = async (file: File, edited = false) => {
    const response = await PostFormRequest<ImageAsset>(API_ROUTES.IMAGE_LIBRARY.CREATE, { image: file, edited })

    if (!response.success || !response.data) {
      throw new Error(response.message)
    }

    return response.data
  }

  const loadAssetFileForCrop = async (url: string, assetId?: number) => {
    const blob = assetId
      ? await GetBlobRequest(API_ROUTES.IMAGE_LIBRARY.CONTENT(assetId))
      : await fetch(url.toClientRequest()).then((response) => {
          if (!response.ok) throw new Error(t("label_image_crop_load_error"))
          return response.blob()
        })

    const hasImageContentType = blob.type.startsWith("image/")

    const hasImageExtension = /\.(png|jpe?g|webp|gif)$/i.test(url.split(/[?#]/)[0])

    if (!hasImageContentType && !hasImageExtension) {
      throw new Error(t("label_image_crop_invalid_file"))
    }

    const extension = hasImageContentType ? getImageExtensionByContentType(blob.type) : getFileExtensionByUrl(url)

    const contentType = hasImageContentType ? blob.type : "image/jpeg"

    return new File([blob], `image-library-${assetId ?? Date.now()}${extension}`, { type: contentType })
  }

  const revokeCropStateUrl = (state: ImageLibraryCropState | null) => {
    if (state?.src.startsWith("blob:")) {
      URL.revokeObjectURL(state.src)
    }
  }

  useEffect(() => {
    if (!enabled || selectedIds.length === 0) {
      setSelectedAssetsMetadataLoaded(true)
      return
    }

    let active = true

    const preloadSelectedAssetsMetadata = async () => {
      try {
        const response = await GetRequest<ImageLibraryResponse>(API_ROUTES.IMAGE_LIBRARY.GET_ALL)

        if (!active || !response.success) return

        setAssets(response.data?.images ?? [])
        
        setMaxProductImages(Math.max(1, response.data?.maxImagesPerProduct ?? 5))
        
        setSelectedAssetsMetadataLoaded(true)
      } catch {
        if (active) setSelectedAssetsMetadataLoaded(false)
      }
    }

    setSelectedAssetsMetadataLoaded(false)
    
    void preloadSelectedAssetsMetadata()

    return () => {
      active = false
    }
  }, [enabled, selectedIds, selectedImagesSignature])

  const handleGetImages = async () => {
    const response = await GetRequest<ImageLibraryResponse>(API_ROUTES.IMAGE_LIBRARY.GET_ALL)

    if (!response.success) {
      throw new Error(response.message)
    }

    const libraryAssets = response.data?.images ?? []

    setMaxProductImages(Math.max(1, response.data?.maxImagesPerProduct ?? 5))

    setAssets(libraryAssets)
    
    setSelectedAssetsMetadataLoaded(true)

    setDraftIds(reconcileSelectedIds(libraryAssets))
  }

  const handleOpen = async () => {
    try {
      enableLoad()
      await handleGetImages()

      setIsOpen(true)
    } catch (error) {
      showError(error)
    } finally {
      disableLoad()
    }
  }

  const handleClose = () => {
    revokeCropStateUrl(cropState)
    setCropState(null)
    setDraftIds([])
    setIsOpen(false)
  }

  const handleConfirm = async () => {
    try {
      enableLoad()
      await onChange(
        selectedAssets.map((asset) => asset.id),
        selectedAssets.map((asset) => asset.url),
      )
      setDraftIds([])
      setIsOpen(false)
    } catch (error) {
      showError(error)
    } finally {
      disableLoad()
    }
  }

  const handleToggle = (asset: ImageAsset) => {
    setDraftIds((current) => {
      if (current.includes(asset.id)) {
        return current.filter((id) => id !== asset.id)
      }

      if (selectionLimit === 1) {
        return [asset.id]
      }

      if (current.length >= selectionLimit) {
        setError(t("general:label_invalid"), t("label_image_limit", { count: selectionLimit }))
        return current
      }

      return [...current, asset.id]
    })
  }

  const handleMove = (index: number, direction: -1 | 1) => {
    setDraftIds((current) => {
      const target = index + direction
      if (target < 0 || target >= current.length) return current

      const next = [...current]
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })
  }

  const handleUpload = async (files: File[]) => {
    if (files.length === 0) return

    try {
      enableLoad()
      const uploaded: ImageAsset[] = []

      for (const file of files) {
        uploaded.push(await uploadImageAsset(file))
      }

      setAssets((current) => [...uploaded, ...current])
      setDraftIds((current) => {
        const availableSlots = Math.max(0, selectionLimit - current.length)
        return [...current, ...uploaded.slice(0, availableSlots).map((asset) => asset.id)]
      })
    } catch (error) {
      showError(error)
    } finally {
      disableLoad()
    }
  }

  const handleAddExternalUrls = async (urls: string[]) => {
    try {
      enableLoad()

      const response = await PostRequest<ImageAsset[]>(API_ROUTES.IMAGE_LIBRARY.CREATE_EXTERNAL, { urls })

      if (!response.success || !response.data) {
        throw new Error(response.message)
      }

      const registeredAssets = response.data

      setAssets((current) => {
        const registeredIds = new Set(registeredAssets.map((asset) => asset.id))

        return [...registeredAssets, ...current.filter((asset) => !registeredIds.has(asset.id))]
      })

      setDraftIds((current) => {
        const availableSlots = Math.max(0, selectionLimit - current.length)

        const newIds = registeredAssets
          .filter((asset) => !current.includes(asset.id))
          .slice(0, availableSlots)
          .map((asset) => asset.id)

        return [...current, ...newIds]
      })

      return true
    } catch (error) {
      showError(error)
      return false
    } finally {
      disableLoad()
    }
  }

  const handleEditAsset = async (asset: ImageAsset, index: number) => {
    if (asset.sourceType === EImageAssetSourceType.Edited) return

    try {
      enableLoad()

      const file = await loadAssetFileForCrop(asset.url, asset.id)

      setCropState({
        file,
        src: URL.createObjectURL(file),
        target: {
          mode: "draft",
          assetId: asset.id,
          index,
        },
      })
    } catch (error) {
      showError(error)
    } finally {
      disableLoad()
    }
  }

  const handleEditSelectedImage = async (index: number) => {
    const url = selectedUrls[index]

    if (!url) {
      return
    }

    try {
      enableLoad()

      const asset = assets.find((item) => item.url === url)

      if (asset?.sourceType === EImageAssetSourceType.Edited) {
        throw new Error(t("label_image_already_edited"))
      }

      const assetId = asset?.id ?? selectedIds[index]

      const file = await loadAssetFileForCrop(url, assetId)

      setCropState({
        file,
        src: URL.createObjectURL(file),
        target: {
          mode: "selected",
          assetId,
          index,
        },
      })
    } catch (error) {
      showError(error)
    } finally {
      disableLoad()
    }
  }

  const handleCancelCrop = () => {
    revokeCropStateUrl(cropState)
    setCropState(null)
  }

  const replaceDraftImage = (target: Extract<ImageLibraryCropTarget, { mode: "draft" }>, uploaded: ImageAsset) => {
    setDraftIds((current) => {
      const currentIndex = current[target.index] === target.assetId ? target.index : current.indexOf(target.assetId)

      if (currentIndex < 0) {
        return current
      }

      const next = [...current]
      next[currentIndex] = uploaded.id

      return next
    })
  }

  const replaceSelectedImage = (target: Extract<ImageLibraryCropTarget, { mode: "selected" }>, uploaded: ImageAsset) => {
    const nextIds = [...selectedIds]
    const nextUrls = [...selectedUrls]

    if (target.index < nextIds.length) {
      nextIds[target.index] = uploaded.id
    } else {
      nextIds.push(uploaded.id)
    }

    nextUrls[target.index] = uploaded.url

    onChange(nextIds, nextUrls)
  }

  const handleConfirmCrop = async (file: File | null) => {
    const activeCropState = cropState

    revokeCropStateUrl(activeCropState)

    setCropState(null)

    if (!file || !activeCropState) {
      return
    }

    try {
      enableLoad()

      const uploaded = await uploadImageAsset(file, true)

      setAssets((current) => [uploaded, ...current])

      if (activeCropState.target.mode === "draft") {
        replaceDraftImage(activeCropState.target, uploaded)
      } else {
        replaceSelectedImage(activeCropState.target, uploaded)
      }
    } catch (error) {
      showError(error)
    } finally {
      disableLoad()
    }
  }

  const handleRemove = async (asset: ImageAsset) => {
    try {
      enableLoad()
      const response = await DeleteRequest<object>(API_ROUTES.IMAGE_LIBRARY.DELETE(asset.id))

      if (!response.success) {
        throw new Error(response.message)
      }

      setAssets((current) => current.filter((item) => item.id !== asset.id))
      setDraftIds((current) => current.filter((id) => id !== asset.id))
    } catch (error) {
      showError(error)
    } finally {
      disableLoad()
    }
  }

  return {
    assets,
    cropState,
    isOpen,
    maxProductImages: selectionLimit,
    selectedAssets,
    selectedAssetsMetadataLoaded,
    selectedIds: draftIds,
    handleCancelCrop,
    handleAddExternalUrls,
    handleClose,
    handleConfirm,
    handleConfirmCrop,
    handleEditAsset,
    handleEditSelectedImage,
    handleMove,
    handleOpen,
    handleRemove,
    handleToggle,
    handleUpload,
  }
}
